import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Title,
  BlackHighlight,
  CTAContainer,
  BannerContentContainer,
  BannerTitle,
  BannerContent,
  BannerHighlight,
} from "./createWorkflowViewer.styles";
import {
  Button,
  Banner,
  useValidation,
  pluralize,
} from "@ssce/ui-shared-library";
import { Settings, WorkflowDataSettings } from "./Settings";
import { WorkflowConfiguration } from "./WorkflowConfiguration";
import { AwaData } from "./createWorkflowSidebars";
import { useCreateWorkflowViewer } from "./createWorkflowViewerHook";
import { LoadingAnimation } from "../../subcomponents/LoadingAnimation";

const validation = (workflowType) => [
  {
    name: [
      (value) => {
        if (!value) return "Name is required";
      },
    ],
    project: [
      (value) => {
        if (!value) return "Project is required";
      },
    ],
    datameshInstance: [
      (value) => {
        if (!value) return "Data mesh instance is required";
      },
    ],
    algoReport: [
      (value) => {
        if (!value) return `${workflowType} is required`;
      },
    ],
    region: [
      (value) => {
        if (!value) return `Region is required`;
      },
    ],
    sleeves: [
      (value) => {
        if (!value) return "Sleeves is required";
      },
    ],
    transactionTiming: [
      (value) => {
        if (!value) return "Transaction timing is required";
      },
    ],
  },
];

const INITIAL_SCHEDULE_INFO = {
  weekly: {
    selectedDatesOfWeek: [],
    HR: "",
    MIN: "",
  },
  monthly: {
    monthlyFreqDay: "",
    HR: "",
    MIN: "",
  },
  cron: {
    cronExpression: "",
  },
  daily: {
    HR: "",
    MIN: "",
  },
  mvSchedule: "",
};

const errorMapper = {
  name: (
    <div>
      <BannerHighlight>Step 1: Settings</BannerHighlight> Name field is
      incomplete.
    </div>
  ),
  project: (
    <div>
      <BannerHighlight>Step 1: Settings</BannerHighlight> Project field is
      incomplete.
    </div>
  ),
  datameshInstance: (
    <div>
      <BannerHighlight>Step 1: Settings</BannerHighlight> Data Mesh Instance
      field is incomplete.
    </div>
  ),
  algoReport: (
    <div>
      <BannerHighlight>Step 1: Settings</BannerHighlight> Algo Report field is
      incomplete.
    </div>
  ),
  region: (
    <div>
      <BannerHighlight>Step 1: Settings</BannerHighlight> Region field is
      incomplete.
    </div>
  ),
};

type WorkflowData = {
  settings: WorkflowDataSettings;
  workflowConfiguration: AwaData;
};

export const CreateWorkflowViewer: React.FC = () => {
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const { errors, validateForm, validateField } = useValidation();
  const { isLoading, hasError, settingsData, createWorkflow } =
    useCreateWorkflowViewer();
  const navigate = useNavigate();
  const [workflowData, setWorkflowData] = useState<WorkflowData>({
    settings: {
      project: "",
      region: "",
      name: "",
      datameshInstance: "",
      algoReport: "",
      schedule: INITIAL_SCHEDULE_INFO,
    },
    workflowConfiguration: {
      sleeves: undefined,
      transactionTiming: undefined,
    },
  });
  const params = useParams();
  const workflowType =
    params.workflowType === "algo-performance"
      ? "SS&C Performance Service"
      : "SS&C Risk Service";

  const getErrorMessages = () => {
    const errorMessages = Object.keys(errors).map((error) => {
      return errorMapper[error];
    });
    if (errors.sleeves || errors.transactionTiming)
      errorMessages.push(
        <div>
          <BannerHighlight>Step 2.1: Configure</BannerHighlight> Create AWA
          Performance Input Data in workflow is incomplete.
        </div>,
      );
    return errorMessages;
  };
  if (isLoading) return <LoadingAnimation />;
  return (
    <div>
      {showErrorAlert && <div></div>}
      {hasError && <>Error</>}
      <Title>
        <div>
          Welcome to Your New <BlackHighlight> {workflowType}</BlackHighlight>
          Workflow
        </div>
      </Title>
      <Settings
        errors={errors}
        workflowType={workflowType}
        selectedSettings={workflowData.settings}
        settingsData={settingsData}
        onChange={(key: string, value: string) => {
          validateField(key, value, validation(workflowType));
          setWorkflowData({
            ...workflowData,
            settings: {
              ...workflowData.settings,
              [key]: value,
            },
          });
        }}
      />
      <WorkflowConfiguration
        awaData={{ ...workflowData.workflowConfiguration }}
        onSubmit={(awaData: AwaData) => {
          setWorkflowData({
            ...workflowData,
            workflowConfiguration: awaData,
          });
        }}
      />
      <CTAContainer>
        <Button
          id={"cancelWorkflow"}
          onClick={() => {
            navigate("/workflow-catalog");
          }}
          variant={"secondary"}
        >
          Cancel
        </Button>
        <Button
          id={"createWorkflow"}
          onClick={() => {
            if (
              validateForm(
                {
                  ...workflowData.settings,
                  ...workflowData.workflowConfiguration,
                },
                validation(workflowType),
              ) &&
              settingsData?.catalogId
            ) {
              createWorkflow({
                ...workflowData,
                catalogId: settingsData.catalogId,
              }).then(() => {
                navigate("/workflow-catalog/new-workflow/confirmation");
              });
            } else {
              setShowErrorAlert(true);
            }
          }}
          variant={"default"}
        >
          Create Workflow
        </Button>
      </CTAContainer>
      {showErrorAlert && (
        <Banner type={"error"}>
          <BannerContentContainer>
            <BannerTitle>Steps Uncompleted</BannerTitle>
            <BannerContent>
              Unfortunately,{" "}
              <BannerHighlight withUnderline>
                {pluralize(Object.keys(errors).length, "error")}
              </BannerHighlight>{" "}
              has prevented the successful creation of this instance. Please
              review the error details listed below or scroll up to identify the
              highlighted issues. <br /> <br />
              {getErrorMessages()}
            </BannerContent>
          </BannerContentContainer>
        </Banner>
      )}
    </div>
  );
};
